import { AnimatePresence, motion } from "framer-motion";
import { forwardRef, useImperativeHandle, useMemo, useRef, useState, type DragEvent, type JSX, type KeyboardEvent } from "react";

import { paletteIn } from "../lib/motion";
import { playCue } from "../lib/sound";
import { useVoss } from "../state/VossProvider";
import { IconButton } from "./primitives";
import { IconMic, IconPaperclip, IconSend } from "./icons";

export interface ComposerHandle {
  focus: () => void;
}

interface SlashItem {
  id: string;
  label: string;
  run: () => void;
}

/** Message composer: slash commands, drag-and-drop, send cue. Upload + voice are UI-only in V1. */
export const Composer = forwardRef<ComposerHandle>(function Composer(_props, ref): JSX.Element {
  const { send, sending, currentWorkspace, workspaces, selectWorkspace, newChat } = useVoss();
  const [value, setValue] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [listening, setListening] = useState(false);
  const [dragging, setDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({ focus: () => textareaRef.current?.focus() }), []);

  const showSlash = value.startsWith("/");
  const slashItems = useMemo<SlashItem[]>(() => {
    if (!showSlash) return [];
    const q = value.slice(1).toLowerCase();
    const clear = (): void => setValue("");
    const items: SlashItem[] = [
      { id: "new", label: "New chat", run: () => { newChat(); clear(); } },
      ...workspaces.map((w) => ({ id: `ws-${w.id}`, label: `Switch to ${w.label}`, run: () => { selectWorkspace(w.id); clear(); } })),
      { id: "clear", label: "Clear input", run: clear },
    ];
    return items.filter((i) => i.label.toLowerCase().includes(q));
  }, [showSlash, value, workspaces, newChat, selectWorkspace]);

  const grow = (el: HTMLTextAreaElement): void => {
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const submit = (): void => {
    if (value.trim().length === 0 || sending) return;
    playCue("send");
    void send(value);
    setValue("");
    setAttachments([]);
    const el = textareaRef.current;
    if (el) el.style.height = "auto";
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (showSlash && slashItems.length > 0 && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      slashItems[0]?.run();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onDrop = (e: DragEvent): void => {
    e.preventDefault();
    setDragging(false);
    setAttachments(Array.from(e.dataTransfer.files).map((f) => f.name));
  };

  return (
    <div className="voss-composer">
      <div
        className="voss-composer__inner"
        data-dragging={dragging}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <AnimatePresence>
          {showSlash && slashItems.length > 0 ? (
            <motion.div className="voss-slash" variants={paletteIn} initial="hidden" animate="visible" exit="exit" role="listbox" aria-label="Slash commands">
              {slashItems.map((item, i) => (
                <button key={item.id} type="button" role="option" aria-selected={i === 0} className="voss-slash__item" data-active={i === 0} onClick={item.run}>
                  <span>/{item.label}</span>
                </button>
              ))}
            </motion.div>
          ) : null}
        </AnimatePresence>
        {dragging ? <div className="voss-composer__drop">Drop files to attach — uploads are UI-only in this release</div> : null}
        {attachments.length > 0 ? (
          <div className="voss-composer__row" style={{ flexWrap: "wrap", gap: "var(--space-2)" }}>
            {attachments.map((name) => (
              <span key={name} className="voss-badge">
                {name}
              </span>
            ))}
            <span className="voss-composer__hint">Uploads are UI-only in this release</span>
          </div>
        ) : null}
        <textarea
          ref={textareaRef}
          className="voss-composer__textarea"
          placeholder={`Message VOSS in ${currentWorkspace?.label ?? "this workspace"}…`}
          rows={1}
          value={value}
          aria-label="Message VOSS"
          onChange={(e) => {
            setValue(e.target.value);
            grow(e.target);
          }}
          onKeyDown={onKeyDown}
        />
        <div className="voss-composer__row">
          <input ref={fileRef} type="file" multiple className="voss-visually-hidden" onChange={(e) => setAttachments(Array.from(e.target.files ?? []).map((f) => f.name))} />
          <IconButton label="Attach files (UI only)" onClick={() => fileRef.current?.click()}>
            <IconPaperclip />
          </IconButton>
          <IconButton label={listening ? "Stop voice (coming soon)" : "Voice input (coming soon)"} aria-pressed={listening} onClick={() => setListening((v) => !v)}>
            <IconMic />
          </IconButton>
          {listening ? <span className="voss-composer__hint">Listening… (voice arrives in a later release)</span> : <span className="voss-composer__hint voss-composer__hint--desktop">Enter to send · Shift+Enter for newline</span>}
          <div className="voss-spacer" />
          <button type="button" className="voss-send" aria-label="Send message" disabled={value.trim().length === 0 || sending} onClick={submit}>
            <IconSend width={18} height={18} />
          </button>
        </div>
      </div>
    </div>
  );
});
